/**
 * スライダー管理モジュール
 * Splideスライダーとカスタムスライドアニメーションを制御
 */

import { gsap } from 'gsap';
import Splide from '@splidejs/splide';
import { Intersection } from '@splidejs/splide-extension-intersection';
import { SPLIDE_CONFIG, SLIDE_CONFIG } from './config.js';

// スライダー用グローバル変数
let activeZoomTween = null;

/**
 * 連続ズームアニメーションの開始
 * @param {Element} slideBg - スライド背景要素
 * @returns {gsap.Tween} GSAPアニメーションオブジェクト
 */
function startContinuousZoom(slideBg) {
  return gsap.to(slideBg, {
    scale: 1.15,
    duration: SLIDE_CONFIG.zoomDuration,
    ease: 'none',
  });
}

/**
 * Splideズームアニメーション付きスライダーのセットアップ
 * MVセクションのスライダーに連続ズーム効果を適用
 */
export function setupSplideZoomAnimation() {
  if (!document.querySelector('#mv .splide')) return;
  
  const splide = new Splide('#mv .splide', SPLIDE_CONFIG.mv);

  // mounted: 初期スライドの背景要素に連続ズーム開始
  splide.on('mounted', function () {
    const slides = splide.Components.Elements.slides;
    slides.forEach(function (slide) {
      const bg = slide.querySelector('.slide_bg');
      if (bg) {
        gsap.set(bg, { scale: 1 });
      }
    });
    const activeSlide = slides[splide.index];
    const activeBg = activeSlide.querySelector('.slide_bg');
    if (activeBg) {
      activeZoomTween = startContinuousZoom(activeBg);
    }
  });

  // move: 切替直前のアニメーション
  splide.on('move', function (newIndex, oldIndex) {
    const slides = splide.Components.Elements.slides;
    const oldBg = slides[oldIndex].querySelector('.slide_bg');
    const newBg = slides[newIndex].querySelector('.slide_bg');
    if (!oldBg || !newBg) return;

    // 連続ズーム停止
    if (activeZoomTween) {
      activeZoomTween.kill();
      activeZoomTween = null;
    }

    // 新背景の初期状態セット（少し拡大＋透明）
    gsap.set(newBg, { opacity: 0, scale: 1.025 });

    gsap
      .timeline()
      .to(
        newBg,
        { scale: 1, opacity: 1, duration: 1.2, ease: 'power1.out' },
        0
      );
  });

  // moved: 切替完了後、新スライドに連続ズームを再スタート
  splide.on('moved', function (newIndex, oldIndex) {
    const slides = splide.Components.Elements.slides;
    const newBg = slides[newIndex].querySelector('.slide_bg');
    if (!newBg) return;

    gsap.set(newBg, { clearProps: 'opacity, scale' });
    activeZoomTween = startContinuousZoom(newBg);
  });

  splide.mount({ Intersection });
}

/**
 * カスタムスライドアニメーションのセットアップ
 * クリップパスを使用したスライド切り替え効果
 */
export function setupSlides() {
  const slider = document.querySelector('.slider');
  const slides = document.querySelectorAll('.slide');
  if (slides.length === 0) return;
  
  let currentIndex = 0;

  // 初期設定
  slides.forEach((slide, index) => {
    gsap.set(slide, {
      clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, -30% 100%)',
      opacity: index === 0 ? 1 : 0,
      scale: index === 0 ? 1.1 : 1,
      zIndex: index === 0 ? 1 : 0,
    });
  });

  // 最初のスライド表示時にオーバーレイを表示
  if (slider) {
    slider.classList.add('active');
  }

  function switchSlide() {
    const currentSlide = slides[currentIndex];
    const nextIndex = (currentIndex + 1) % slides.length;
    const nextSlide = slides[nextIndex];

    // GSAP Timelineを作成
    const timeline = gsap.timeline({
      onComplete: () => {
        currentIndex = nextIndex;
        switchSlide();
      },
    });

    // スライドアニメーション
    timeline
      .set(nextSlide, {
        opacity: 0,
        clipPath: 'polygon(130% 0%, 100% 0%, 100% 100%, 100% 100%)',
        zIndex: 2,
        scale: 1,
      })
      .to(nextSlide, {
        opacity: 1,
        duration: 0.5,
        ease: 'power2.inOut',
      })
      .to(nextSlide, {
        clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, -30% 100%)',
        duration: 1.5,
        ease: 'power2.inOut',
      })
      .to(
        currentSlide,
        {
          opacity: 0,
          duration: 1.5,
          ease: 'power2.inOut',
        },
        '<'
      )
      .to(
        nextSlide,
        {
          scale: 1.1,
          duration: SLIDE_CONFIG.switchInterval,
          ease: 'none',
        },
        '<'
      )
      .set(currentSlide, {
        scale: 1,
        zIndex: 0,
      });
  }

  switchSlide();
} 

/**
 * 背景スライダーの初期化（CSS Animation使用）
 * 途切れることなく流れ続ける背景スライダー
 * @returns {void}
 */
export function setupBackgroundSlider() {
  const slider = document.querySelector('.mv_bg_slider');
  if (!slider) return;

  const track = slider.querySelector('.mv_bg_slider_track');
  const slides = slider.querySelectorAll('.mv_bg_slide');
  
  if (!track || slides.length === 0) return;
  
  // CSSアニメーションで制御されるため、JavaScript側では特別な処理は不要
  // 必要に応じてアニメーションの制御（再生/停止）をここに追加可能
}

export function setupBannerCarousel() {
  /**
   * バナーカルーセル（Banner Carousel）の初期化
   * Splide.js を利用し左右に少しだけ次のバナーが覗くカルーセルを生成する
   * 中央2枚表示のカルーセル
   * @returns {void}
   */
  const carousels = [
    {
      selector: '#bannerCarouselTwo',
      perPage: 2,
      padding: { left: '10%', right: '10%' },
    },
  ];

  carousels.forEach(({ selector, perPage, padding }) => {
    const el = document.querySelector(selector);
    if (!el) return;

    const splide = new Splide(el, {
      type: 'loop',
      perPage,
      focus: 'center',
      // 自動再生間隔を遅めに調整（8秒）
      interval: 8000,
      // スライド遷移スピードも緩やかに（700ms）
      speed: 700,
      arrows: true,
      pagination: false,
      gap: '1rem',
      padding,
      breakpoints: {
        767: {
          perPage: 1,
          padding: { left: '15%', right: '15%' },
        },
      },
    });

    splide.mount();
  });
}

/**
 * 学校スライダーの初期化
 * 右端が少し見切れるデザインでスライダーを実装
 * @returns {void}
 */
export function setupSchoolSlider() {
  const el = document.querySelector('#schoolSlider');
  if (!el) return;

  const splide = new Splide(el, {
    type: 'loop', // 無限ループスライダーに変更
    perPage: 3,
    perMove: 1,
    focus: 'left',
    arrows: true, // arrowを表示
    pagination: true,
    gap: '1rem',
    padding: { right: '5rem' },
    autoplay: false, // 自動再生を停止
    speed: 800,
    // rewind: true, // loopタイプでは不要
    // ページネーション数を正しく制限
    classes: {
      pagination: 'splide__pagination school-slider__pagination',
    },
    breakpoints: {
      1024: {
        perPage: 2,
        perMove: 1,
        padding: { right: '3rem' },
      },
      575: {
        perPage: 1,
        perMove: 1,
        padding: { right: '4rem' },
      },
    },
  });

  // loopタイプでのページネーション調整
  splide.on('pagination:mounted', function (data) {
    // loopタイプでは元のスライド数に基づいてページネーションを制限
    const totalSlides = 6;
    const paginationItems = data.items;
    
    // 6個のスライドに対して6個のページネーションが表示されるのを制限
    if (paginationItems.length > totalSlides) {
      for (let i = totalSlides; i < paginationItems.length; i++) {
        if (paginationItems[i] && paginationItems[i].button) {
          paginationItems[i].button.style.display = 'none';
        }
      }
    }
  });

  splide.mount();
}

/**
 * コラムスライダーの初期化
 * 右端が少し見切れるデザインでスライダーを実装
 * @returns {void}
 */
export function setupColumnSlider() {
  const el = document.querySelector('#columnSlider');
  if (!el) return;

  const splide = new Splide(el, {
    type: 'loop', // 無限ループスライダー（学校スライダーと同じ）
    perPage: 3,
    perMove: 1,
    focus: 'left',
    arrows: true, // arrowを表示
    pagination: true, // ページネーションを有効にして波々インジケーター表示
    gap: '1rem',
    padding: { right: '5rem' }, // 右端の見切れ部分を大きくする（学校スライダーと同じ）
    autoplay: false, // 自動再生を停止
    speed: 800,
    // ページネーション数を正しく制限
    classes: {
      pagination: 'splide__pagination column-slider__pagination',
    },
    breakpoints: {
      1024: {
        perPage: 2,
        perMove: 1,
        padding: { right: '3rem' }, // 学校スライダーと同じ
      },
      575: {
        perPage: 1,
        perMove: 1,
        padding: { right: '4rem' }, // 学校スライダーと同じ
      },
    },
  });

  // loopタイプでのページネーション調整（学校スライダーと同じロジック）
  splide.on('pagination:mounted', function (data) {
    // loopタイプでは元のスライド数に基づいてページネーションを制限
    const totalSlides = 6; // コラム数
    const paginationItems = data.items;
    
    // 6個のスライドに対して6個のページネーションが表示されるのを制限
    if (paginationItems.length > totalSlides) {
      for (let i = totalSlides; i < paginationItems.length; i++) {
        if (paginationItems[i] && paginationItems[i].button) {
          paginationItems[i].button.style.display = 'none';
        }
      }
    }
  });

  splide.mount();
} 
